/**
 * Floor.vueで使用するユーティリティ関数
 * テスト可能にするためにsetupから切り出し
 */

// 残り時間の警告閾値（ミリ秒）
// - EXPIRING_SOON: サイドパネル等で残り時間を「警告」(赤テキスト) 表示する閾値
export const EXPIRING_SOON_THRESHOLD_MS = 5 * 60000

export const formatElapsed = (startAt, now) => {
  if (!startAt) return '--:--'
  const diff = now - new Date(startAt).getTime()
  if (diff < 0) return '00:00'
  const h = Math.floor(diff / 3600000)
  const m = Math.floor((diff % 3600000) / 60000)

  return `${h}:${String(m).padStart(2, '0')}`
}

export const formatRemaining = (expireAt, now) => {
  if (!expireAt) return ''
  const diff = new Date(expireAt).getTime() - now
  if (diff <= 0) return '超過'
  const m = Math.floor(diff / 60000)

  return `残${m}分`
}

export const isExpiringSoon = (expireAt, now) => {
  if (!expireAt) return false
  const diff = new Date(expireAt).getTime() - now

  return diff <= EXPIRING_SOON_THRESHOLD_MS
}

/**
 * 卓終了アラート設定に基づく終了ステータスを返す。
 * Club の firstFinishNoticeMinutes / secondFinishNoticeMinutes（いずれも「終了の何分前」）と
 * 卓の expireAt（終了予定時刻）から、残り時間に応じた色ステータスを判定する。
 *
 * - first > second（1回目の方が大きい値）が前提（Club バリデーションで担保）
 * - first / second のいずれかが未設定（falsy）ならアラート無効とみなし null を返す
 *
 * @param {string|null} expireAt 終了予定時刻（ISO8601）
 * @param {number|null} first    1回目アラート（終了の何分前 = 黄 / ending）
 * @param {number|null} second   2回目アラート（終了の何分前 = 赤 / expired）
 * @param {number} now           現在時刻（ms epoch）
 * @returns {'expired'|'ending'|null} expired=赤 / ending=黄 / null=対象外
 */
export const getFinishNoticeStatus = (expireAt, first, second, now) => {
  if (!expireAt || !first || !second) return null
  const endTime = new Date(expireAt).getTime() - now
  if (endTime < second * 60000) return 'expired'
  if (endTime < first * 60000) return 'ending'

  return null
}

export const isActiveTimecard = timecard => {
  if (!timecard?.attributes) return false

  return !timecard.attributes.leaveAt
}

export const isCast = user => user?.attributes?.role === 'cast'

/**
 * タイムカードに設定された勤怠ステータス名を取り出す（欠勤・遅刻など）。
 * waiter timecards API のシリアライザが meta.timecardStatusName に名前を返す。
 * 未設定なら null。
 *
 * @param {object} timecard タイムカード（serializableHash の data 要素）
 * @returns {string|null}
 */
export const getTimecardStatusName = timecard => timecard?.meta?.timecardStatusName || null

/**
 * 勤怠ステータス付きキャストを、ステータス名ごとにグルーピングする。
 * 「欠勤かどうか」の判定は行わず、_status（ステータス名）が設定されたキャストを
 * そのステータス名で束ねる。グループの並びはステータスの初出順を維持する。
 *
 * @param {Array<{_status?: string}>} casts キャスト配列（_status を持ちうる）
 * @returns {Array<{name: string, casts: Array}>}
 */
export const groupCastsByStatus = casts => {
  const order = []
  const map = {}
  const list = casts || []
  list.forEach(c => {
    if (!c?._status) return
    if (!map[c._status]) {
      map[c._status] = []
      order.push(c._status)
    }
    map[c._status].push(c)
  })

  return order.map(name => ({ name, casts: map[name] }))
}

export const parseStoredShowAllRoles = stored => stored === 'true'

export const formatTime = dt => {
  if (!dt) return ''
  const d = new Date(dt)

  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

export const parseStoredCompactMode = stored => stored === 'true'

export const buildFreeCastUserIds = (currentIds, castId) => {
  const ids = currentIds || []
  const id = Number(castId)
  if (ids.map(Number).includes(id)) return null

  return [...ids, id]
}

export const buildNominationVendingHistoryParams = (nomination, castId) => ({
  vendibleType: 'Nomination',
  vendibleId: nomination.id,
  sellingPrice: nomination.attributes.sellingPrice || 0,
  taxCharge: nomination.attributes.taxCharge || 'normal',
  isNonExistVendible: false,
  vendibleRecipientsAttributes: [{ userId: Number(castId), ratio: 1 }],
})

const DUMMY_CAST_NAMES = ['サンプルA', 'サンプルB', 'サンプルC', 'サンプルD']
const DUMMY_VENDIBLE_NAMES = ['本指名', '場内指名', '同伴指名']

export const generateDummyCasts = count => {
  const result = []
  for (let i = 0; i < count; i += 1) {
    if (i === 0) {
      result.push({ type: 'free', userId: -1, name: DUMMY_CAST_NAMES[0] })
    } else {
      result.push({
        type: 'nomination',
        userId: -(i + 1),
        name: DUMMY_CAST_NAMES[i % DUMMY_CAST_NAMES.length],
        vendibleName: DUMMY_VENDIBLE_NAMES[(i - 1) % DUMMY_VENDIBLE_NAMES.length],
      })
    }
  }

  return result
}

// cautionメモを持つ顧客がセットされているか(卓カードの注意マーク用)
export const hasCautionCustomer = table => {
  return (table?.attributes?.customers || []).some(customer => customer.hasCautionNote)
}

export const splitCastsForDisplay = (casts, maxVisible = 2) => ({
  visible: casts.slice(0, maxVisible),
  overflowCount: Math.max(0, casts.length - maxVisible),
})

export const getTableCasts = (table, users) => {
  const result = []
  // フリー（cast_user_ids）
  const castIds = table?.attributes?.castUserIds || []
  castIds.forEach(uid => {
    const user = users.find(u => Number(u.id) === Number(uid))
    if (user) {
      result.push({
        type: 'free',
        userId: Number(uid),
        name: user.attributes?.name || `ID:${uid}`,
      })
    }
  })
  // 指名（currentNominations）
  const noms = table?.meta?.currentNominations?.data || []
  noms.forEach(nom => {
    const recipients = nom?.attributes?.vendibleRecipients?.data || []
    const recipientCount = recipients.length
    recipients.forEach(r => {
      const name = r?.attributes?.recipient?.data?.attributes?.name
      const uid = r?.attributes?.userId
      if (name && !result.find(c => c.userId === Number(uid))) {
        result.push({
          type: 'nomination',
          userId: Number(uid),
          vendingHistoryId: nom.id,
          vendibleId: nom?.attributes?.vendibleId,
          vendibleRecipientId: r.id,
          recipientCount,
          name,
          vendibleName: nom?.attributes?.vendibleName || '指名',
        })
      }
    })
  })

  return result
}

/**
 * フロアレイアウトが1つでも設計されているかを判定する。
 *
 * フロアビュー画面を開いたとき、全ホールの tableLayouts が空なら
 * 卓の配置がないため画面として意味を成さない。この場合フロアレイアウト
 * 設定画面に誘導する。
 *
 * @param {Array} halls  HallApi.getHalls のレスポンス (halls.data)
 * @returns {boolean}
 */
export const isFloorLayoutConfigured = halls => {
  if (!Array.isArray(halls) || halls.length === 0) return false

  return halls.some(hall => {
    const layouts = hall?.attributes?.tableLayouts

    return Array.isArray(layouts) && layouts.length > 0
  })
}
